import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, TrashSimple, FilePdf } from "@phosphor-icons/react";
import SupplierAutocomplete from "@/components/SupplierAutocomplete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UNITS = ["adet", "kg", "ton", "m", "m2", "m3", "lt", "paket", "kutu", "torba"];

const formatErr = (e) => e?.response?.data?.detail || e?.message || "Hata oluştu";

/**
 * Reusable Contract Edit Dialog — Sözleşme temel bilgileri + kalemler
 * + Sipariş Teyit Formu bilgileri hepsi tek diyalogda.
 * Contracts.jsx ve ContractDetail.jsx birlikte kullanır.
 */
export default function ContractEditDialog({
  open, onOpenChange, contract, onSaved,
  suppliers: suppliersProp, sites: sitesProp,
}) {
  const [suppliers, setSuppliers] = useState(suppliersProp || []);
  const [sites, setSites] = useState(sitesProp || []);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => contractToForm(contract));
  const [confForm, setConfForm] = useState(() => contractToConfForm(contract));
  // Canlı önizleme
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(contractToForm(contract));
      setConfForm(contractToConfForm(contract));
    }
  }, [open, contract]);

  useEffect(() => {
    if (!open) return;
    if (!suppliersProp) axios.get(`${API}/suppliers`).then(r => setSuppliers(r.data)).catch(() => {});
    if (!sitesProp) axios.get(`${API}/sites`).then(r => setSites(r.data)).catch(() => {});
  }, [open, suppliersProp, sitesProp]);

  // Canlı PDF önizleme — form değiştikçe 700ms debounce ile yeniden üret.
  // Sadece EDIT modunda (mevcut sözleşme id'si varsa) çalışır.
  useEffect(() => {
    if (!open || !contract?.id) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const items = form.items
          .filter(it => it.material_name && it.material_name.trim() && parseFloat(it.quantity) > 0)
          .map(it => ({
            material_name: it.material_name.trim(),
            unit: it.unit || "adet",
            quantity: parseFloat(it.quantity) || 0,
            unit_price: parseFloat(it.unit_price) || 0,
            vat_rate: parseFloat(it.vat_rate) || 0,
          }));
        const payload = {
          supplier_id: form.supplier_id, supplier_name: form.supplier_name,
          site_id: form.site_id, site_name: form.site_name,
          items: items.length > 0 ? items : null,
          start_date: form.start_date, end_date: form.end_date,
          status: form.status, notes: form.notes,
          confirmation_data: confForm,
        };
        const r = await axios.post(
          `${API}/contracts/${contract.id}/full-preview.pdf`,
          payload,
          { responseType: "blob" },
        );
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        setPreviewUrl(old => {
          if (old) { try { URL.revokeObjectURL(old); } catch { /* noop */ } }
          return url;
        });
      } catch { /* önizleme sessizce başarısız olabilir */ }
      finally { if (!cancelled) setPreviewLoading(false); }
    }, 700);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, form, confForm, contract?.id]);

  // Dialog kapanınca blob URL'i temizle
  useEffect(() => {
    if (open) return;
    if (previewUrl) {
      try { URL.revokeObjectURL(previewUrl); } catch { /* noop */ }
      setPreviewUrl("");
    }
  }, [open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setC = (k, v) => setConfForm(f => ({ ...f, [k]: v }));

  const addItem = () => setForm(f => ({
    ...f,
    items: [...f.items, { material_name: "", unit: "adet", quantity: "", unit_price: "", vat_rate: 20 }],
  }));
  const removeItem = (idx) => setForm(f => ({
    ...f,
    items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items,
  }));
  const updateItem = (idx, key, value) => setForm(f => ({
    ...f,
    items: f.items.map((it, i) => i === idx ? { ...it, [key]: value } : it),
  }));

  const save = async () => {
    if (!form.supplier_id || !form.site_id) {
      toast.error("Firma ve şantiye zorunlu");
      return;
    }
    const validItems = form.items.filter(it => it.material_name?.trim() && parseFloat(it.quantity) > 0);
    if (validItems.length === 0) {
      toast.error("En az bir kalem eklemelisiniz");
      return;
    }
    const first = validItems[0];
    const payload = {
      supplier_id: form.supplier_id, supplier_name: form.supplier_name,
      site_id: form.site_id, site_name: form.site_name,
      material_name: first.material_name,
      unit: first.unit,
      total_quantity: parseFloat(first.quantity) || 0,
      unit_price: parseFloat(first.unit_price) || 0,
      vat_rate: parseFloat(first.vat_rate) || 0,
      items: validItems.map(it => ({
        material_name: it.material_name.trim(),
        unit: it.unit || "adet",
        quantity: parseFloat(it.quantity) || 0,
        unit_price: parseFloat(it.unit_price) || 0,
        vat_rate: parseFloat(it.vat_rate) || 0,
      })),
      start_date: form.start_date, end_date: form.end_date,
      status: form.status, notes: form.notes,
    };
    setSaving(true);
    try {
      if (contract?.id) {
        // Önce sözleşme temel bilgilerini güncelle, ardından teyit formu bilgilerini PATCH et
        await axios.put(`${API}/contracts/${contract.id}`, payload);
        await axios.patch(`${API}/contracts/${contract.id}/confirmation-data`, confForm);
      } else {
        const r = await axios.post(`${API}/contracts`, payload);
        // Yeni sözleşme için de teyit formu bilgilerini kaydet (varsa)
        const newId = r?.data?.id;
        const hasConfData = Object.values(confForm).some(v => (v || "").toString().trim() !== "");
        if (newId && hasConfData) {
          await axios.patch(`${API}/contracts/${newId}/confirmation-data`, confForm).catch(() => {});
        }
      }
      toast.success(contract?.id ? "Sözleşme ve teyit formu bilgileri güncellendi" : "Sözleşme oluşturuldu");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:rounded-sm w-[97vw] max-w-none h-[96vh] max-h-none overflow-hidden p-0 flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-200 flex-shrink-0">
          <DialogTitle className="font-heading">
            {contract?.id ? "Sözleşmeyi Düzenle" : "Yeni Sözleşme"}
          </DialogTitle>
          {contract?.id && (
            <div className="text-xs text-slate-500 mt-1">
              Sol tarafta düzenlediğiniz alanlar sağdaki Sipariş Teyit Formu önizlemesine canlı yansır.
            </div>
          )}
        </DialogHeader>
        <div className={contract?.id ? "grid grid-cols-1 lg:grid-cols-[minmax(0,42%)_minmax(0,58%)] gap-0 flex-1 min-h-0" : "flex-1 min-h-0 overflow-y-auto"}>
          {/* SOL: Form */}
          <div className={`p-6 overflow-y-auto min-h-0 ${contract?.id ? "border-r border-slate-200" : ""}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Firma</Label>
            <div className="mt-2">
              <SupplierAutocomplete
                value={form.supplier_id}
                suppliers={suppliers}
                onChange={(s) => setForm(f => ({
                  ...f, supplier_id: s?.id || "", supplier_name: s?.name || "",
                }))}
                placeholder="Firma adı yazın (Örn: Dem)…"
                testId="ce-supplier"
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Şantiye</Label>
            <Select
              value={form.site_id}
              onValueChange={(v) => {
                const s = sites.find(x => x.id === v);
                setForm(f => ({ ...f, site_id: v, site_name: s?.name || "" }));
              }}
            >
              <SelectTrigger className="mt-2 rounded-none sm:rounded-sm" data-testid="ce-site">
                <SelectValue placeholder="Şantiye seçin" />
              </SelectTrigger>
              <SelectContent>
                {sites.filter(s => s.status === "aktif").map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Kalemler ({form.items.length})</Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem}
                      data-testid="ce-add-item"
                      className="rounded-none sm:rounded-sm text-[10px] uppercase tracking-widest font-bold border-[#1B4776] text-[#1B4776] hover:bg-[#1B4776] hover:text-white h-7">
                <Plus size={12} className="mr-1" /> Kalem Ekle
              </Button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, idx) => (
                <div key={idx}
                     data-testid={`ce-item-row-${idx}`}
                     className="grid grid-cols-12 gap-2 items-start p-2 border border-slate-200 bg-slate-50 rounded-none sm:rounded-sm">
                  <div className="col-span-12 sm:col-span-5">
                    <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Malzeme</Label>
                    <Input value={it.material_name}
                           onChange={e => updateItem(idx, "material_name", e.target.value)}
                           placeholder="Örn: Çimento CEM II 42.5R"
                           className="mt-1 rounded-none sm:rounded-sm h-9 bg-white" />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Miktar</Label>
                    <Input type="number" step="0.01" value={it.quantity}
                           onChange={e => updateItem(idx, "quantity", e.target.value)}
                           className="mt-1 rounded-none sm:rounded-sm h-9 bg-white" />
                  </div>
                  <div className="col-span-4 sm:col-span-1">
                    <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Birim</Label>
                    <Select value={it.unit} onValueChange={(v) => updateItem(idx, "unit", v)}>
                      <SelectTrigger className="mt-1 rounded-none sm:rounded-sm h-9 bg-white text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Birim Fiyat (₺)</Label>
                    <Input type="number" step="0.01" value={it.unit_price}
                           onChange={e => updateItem(idx, "unit_price", e.target.value)}
                           className="mt-1 rounded-none sm:rounded-sm h-9 bg-white" />
                  </div>
                  <div className="col-span-8 sm:col-span-1">
                    <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">KDV %</Label>
                    <Input type="number" step="1" value={it.vat_rate}
                           onChange={e => updateItem(idx, "vat_rate", e.target.value)}
                           className="mt-1 rounded-none sm:rounded-sm h-9 bg-white" />
                  </div>
                  <div className="col-span-4 sm:col-span-1 flex items-end justify-center pt-6">
                    {form.items.length > 1 && (
                      <button type="button"
                              onClick={() => removeItem(idx)}
                              className="w-9 h-9 flex items-center justify-center hover:bg-red-50 border border-slate-300"
                              title="Bu kalemi sil">
                        <TrashSimple size={14} className="text-red-600" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {form.items.some(it => parseFloat(it.quantity) > 0 && parseFloat(it.unit_price) > 0) && (
              <div className="mt-3 p-2 bg-slate-100 border border-slate-200 text-xs font-mono flex justify-end gap-4">
                <span>Ara Toplam: ₺{form.items.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                <span className="font-bold text-[#1B4776]">
                  KDV Dahil: ₺{form.items.reduce((s, it) => {
                    const line = (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0);
                    return s + line * (1 + (parseFloat(it.vat_rate) || 0) / 100);
                  }, 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Durum</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="mt-2 rounded-none sm:rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="aktif">Aktif</SelectItem>
                <SelectItem value="tamamlandi">Tamamlandı</SelectItem>
                <SelectItem value="iptal">İptal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Başlangıç</Label>
            <Input type="date" value={form.start_date}
                   onChange={e => set("start_date", e.target.value)}
                   className="mt-2 rounded-none sm:rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Bitiş</Label>
            <Input type="date" value={form.end_date}
                   onChange={e => set("end_date", e.target.value)}
                   className="mt-2 rounded-none sm:rounded-sm" />
          </div>

          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Notlar</Label>
            <Textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2}
                      className="mt-2 rounded-none sm:rounded-sm" />
          </div>

          {/* ─────────────────────────────────────────────────────────── */}
          {/* Sipariş Teyit Formu Bilgileri — PDF'te görünecek alanlar     */}
          {/* ─────────────────────────────────────────────────────────── */}
          <div className="md:col-span-2 mt-2 pt-4 border-t-2 border-dashed border-slate-300">
            <div className="mb-3">
              <div className="text-xs uppercase tracking-widest font-bold text-[#1B4776]">
                Sipariş Teyit Formu Bilgileri
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                Bu alanlar PDF&apos;te &quot;Teslimat&quot;, &quot;İlgili Kişi&quot;, &quot;Nakliye&quot;, &quot;Ödeme&quot; ve &quot;İlave Açıklamalar&quot; olarak görünür.
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Teslimat Adresi</Label>
            <Textarea value={confForm.delivery_address} onChange={e => setC("delivery_address", e.target.value)}
                      rows={2} data-testid="ce-delivery-address"
                      className="mt-1 rounded-none sm:rounded-sm" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">İlgili Kişi</Label>
            <Input value={confForm.contact_person} onChange={e => setC("contact_person", e.target.value)}
                   data-testid="ce-contact-person" className="mt-1 rounded-none sm:rounded-sm" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">İlgili Kişi Telefonu</Label>
            <Input value={confForm.contact_phone} onChange={e => setC("contact_phone", e.target.value)}
                   data-testid="ce-contact-phone" className="mt-1 rounded-none sm:rounded-sm" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Teslimat Tarihi (serbest metin)</Label>
            <Input value={confForm.delivery_date_text} onChange={e => setC("delivery_date_text", e.target.value)}
                   placeholder="Örn: 15.07.2026 - 30.07.2026 / Program dahilinde"
                   data-testid="ce-delivery-date-text" className="mt-1 rounded-none sm:rounded-sm" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Nakliye</Label>
            <Textarea value={confForm.shipping_note} onChange={e => setC("shipping_note", e.target.value)}
                      rows={2}
                      placeholder="Örn: Fiyata dahildir. / Tedarikçiye aittir."
                      data-testid="ce-shipping-note" className="mt-1 rounded-none sm:rounded-sm" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Ödeme Türü</Label>
            <Input value={confForm.payment_type} onChange={e => setC("payment_type", e.target.value)}
                   placeholder="Örn: Peşin / Vadeli"
                   data-testid="ce-payment-type" className="mt-1 rounded-none sm:rounded-sm" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Vade Şekli</Label>
            <Input value={confForm.payment_terms_text} onChange={e => setC("payment_terms_text", e.target.value)}
                   placeholder="Örn: Fatura + 60 Gün"
                   data-testid="ce-payment-terms-text" className="mt-1 rounded-none sm:rounded-sm" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">İlave Açıklamalar</Label>
            <Textarea value={confForm.additional_notes} onChange={e => setC("additional_notes", e.target.value)}
                      rows={5}
                      placeholder="PDF'te 'İlave Açıklamalar' bölümüne yazılacak metin (satır satır)"
                      data-testid="ce-additional-notes" className="mt-1 rounded-none sm:rounded-sm" />
          </div>
        </div>
          </div>
          {/* SAĞ: Canlı PDF Önizleme (sadece edit modunda) */}
          {contract?.id && (
            <div className="bg-slate-100 flex flex-col h-full min-h-0" data-testid="ce-preview-panel">
              <div className="px-4 py-2 border-b border-slate-200 bg-white flex items-center justify-between text-[10px] uppercase tracking-widest font-bold text-slate-600">
                <span className="flex items-center gap-1.5">
                  <FilePdf size={12} /> Sipariş Teyit Formu — Canlı Önizleme
                </span>
                <div className="flex items-center gap-3">
                  {previewUrl && (
                    <a href={previewUrl} target="_blank" rel="noreferrer"
                       data-testid="ce-preview-open-new-tab"
                       className="text-[10px] normal-case tracking-normal text-[#1B4776] hover:underline font-normal">
                      Yeni sekmede aç ↗
                    </a>
                  )}
                  <span className={`text-[10px] normal-case tracking-normal ${previewLoading ? "text-[#1B4776]" : "text-slate-400"}`}>
                    {previewLoading ? "Güncelleniyor…" : "Güncel"}
                  </span>
                </div>
              </div>
              <div className="flex-1 relative min-h-0">
                {previewUrl ? (
                  <iframe
                    key={previewUrl}
                    src={`${previewUrl}#zoom=page-width&toolbar=1`}
                    title="Canlı Sipariş Teyit Formu Önizleme"
                    data-testid="ce-preview-iframe"
                    className="absolute inset-0 w-full h-full border-0 bg-white"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400">
                    Önizleme hazırlanıyor…
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="px-6 py-3 border-t border-slate-200 bg-white flex-shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  className="rounded-none sm:rounded-sm">İptal</Button>
          <Button onClick={save} disabled={saving} data-testid="ce-save"
                  className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153a63] text-white">
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function contractToForm(c) {
  if (!c) {
    return {
      supplier_id: "", supplier_name: "",
      site_id: "", site_name: "",
      items: [{ material_name: "", unit: "adet", quantity: "", unit_price: "", vat_rate: 20 }],
      start_date: "", end_date: "",
      status: "aktif", notes: "",
    };
  }
  const items = (c.items && c.items.length > 0)
    ? c.items.map(it => ({
        material_name: it.material_name || "",
        unit: it.unit || "adet",
        quantity: it.quantity || "",
        unit_price: it.unit_price || "",
        vat_rate: it.vat_rate !== undefined ? it.vat_rate : 20,
      }))
    : [{
        material_name: c.material_name || "",
        unit: c.unit || "adet",
        quantity: c.total_quantity || "",
        unit_price: c.unit_price || "",
        vat_rate: c.vat_rate !== undefined ? c.vat_rate : 20,
      }];
  return {
    supplier_id: c.supplier_id || "", supplier_name: c.supplier_name || "",
    site_id: c.site_id || "", site_name: c.site_name || "",
    items,
    start_date: c.start_date || "", end_date: c.end_date || "",
    status: c.status || "aktif", notes: c.notes || "",
  };
}

function contractToConfForm(c) {
  const cd = (c && c.confirmation_data) || {};
  return {
    delivery_address: cd.delivery_address || "",
    contact_person: cd.contact_person || "",
    contact_phone: cd.contact_phone || "",
    delivery_date_text: cd.delivery_date_text || "",
    shipping_note: cd.shipping_note || "",
    payment_type: cd.payment_type || "",
    payment_terms_text: cd.payment_terms_text || "",
    additional_notes: cd.additional_notes || "",
  };
}
